/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { FormattedMessage } from 'react-intl-next';

import { Inline, Text } from '@atlaskit/primitives/compiled';

import { VerifiedTeamLozenge } from '@confluence/space-utils/entry-points/VerifiedTeamLozenge';
import { fg } from '@confluence/feature-gating';

import type {
	GroupCountComponentAccessDetailsGroupCountsQuery as GroupCountComponentAccessDetailsGroupCountsQuery$data,
	GroupCountComponentAccessDetailsGroupCountsQueryVariables as GroupCountComponentAccessDetailsGroupCountsQuery$variables,
} from './__types__/GroupCountComponentAccessDetailsGroupCountsQuery';

const GroupCountComponentAccessDetailsGroupCountsQuery = gql`
	query GroupCountComponentAccessDetailsGroupCountsQuery($groupIdList: [String]) {
		groupCounts(groupIds: $groupIdList) {
			groupCounts {
				key
				value
			}
		}
	}
`;

type GroupCountComponentProps = {
	groupId?: string | null;
	isGroup?: boolean;
	isVerified?: boolean;
};

const useGroupCount = (id: GroupCountComponentProps['groupId']) => {
	const { loading, data } = useQuery<
		GroupCountComponentAccessDetailsGroupCountsQuery$data,
		GroupCountComponentAccessDetailsGroupCountsQuery$variables
	>(GroupCountComponentAccessDetailsGroupCountsQuery, {
		variables: { groupIdList: id !== undefined ? [id] : null },
		skip: !id,
	});
	if (loading) return null;

	const count = data?.groupCounts?.groupCounts?.[0]?.value;

	if (typeof count === 'number') {
		return count;
	}
};

export const GroupCountComponent: FC<GroupCountComponentProps> = ({
	groupId,
	isGroup,
	isVerified,
}) => {
	const count = useGroupCount(groupId);
	const teamOrGroupByline = () => {
		if (isGroup) {
			return (
				<FormattedMessage
					id="space-access-details.access-details-table.header.admin-group"
					defaultMessage="Admin group"
					description="Byline label for an admin group"
				/>
			);
		} else {
			return isVerified ? (
				<FormattedMessage
					id="space-access-details.access-details-table.header.official-team"
					defaultMessage="Official team"
					description="Byline label for an official team"
				/>
			) : (
				<FormattedMessage
					id="space-access-details.access-details-table.header.team"
					defaultMessage="Team"
					description="Byline label for a team"
				/>
			);
		}
	};
	if (!count) {
		return null;
	}

	return (
		<Inline testId="group-count-component" space="space.050" alignBlock="center">
			{fg('confluence_frontend_perms_exp_teams_as_principals') ? (
				<>
					{' '}
					<Text color="color.text.subtlest" size="small" testId="member-count-subtext">
						{teamOrGroupByline()}
					</Text>
					{(isVerified || isGroup) && <VerifiedTeamLozenge isTeam={!isGroup} spacing="none" />}
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage
							id="space-access-details.access-details-table.header.principal"
							defaultMessage="• {count, plural, one {# member} other {# members}}"
							description="Count of people in a given group"
							values={{
								count,
							}}
						/>
					</Text>
				</>
			) : (
				<FormattedMessage
					id="space-access-details.access-details-table.header.principal"
					defaultMessage="{count, plural, one {# person} other {# people}}"
					description="Count of people in a given group"
					values={{
						count,
					}}
				/>
			)}
		</Inline>
	);
};
